import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import { parseJsonString } from 'pano-utils';
import { useImmer } from 'use-immer';
import { useStore, useStoreMap } from 'effector-react';
import { createEvent } from 'effector';
import { debounce } from 'patronum/debounce';
import { API_DOMAIN } from '@constants';
import { PrivilegeType, type TourData } from '@types';
import Modal from '@components/Modal';
import Title from '@components/Title';
import Icon from '@components/Icon';
import axios from '@utils/axios';
import getTid from '@utils/getTid';
import { $tourData } from '@stores/dataStores';
import { $isTeamAccount, $isVip } from '@stores/memberStores';
import useToggle from '@hooks/useToggle';
import { teamConfig, useTeamConfig } from '@utils/teamConfig';
import IconArrow from './img-arrow.png';
import IconVip from './img-vip.png';
import { ShareBasic } from './ShareBasic';
import type { ShareConfig, ShareSettingData } from './types';
import { ShareInfoSetting } from './ShareInfoSetting';
import { ShareTempLinks } from './ShareTempLinks';

interface Props {
  onRequestClose: () => void;
}

const navItems = ['基础分享', '临时分享', '分享配置'] as const;

const saveEvent = createEvent<ShareSettingData>();
const configSave = debounce({ source: saveEvent, timeout: 500 });
configSave.watch(async (requestData) => {
  try {
    const url = teamConfig(
      `${API_DOMAIN}/member/my/tour/${getTid()}`,
      `${API_DOMAIN}/team/app/tours/${getTid()}`
    );
    await axios.post<TourData>(url, requestData);
  } catch (e) {
    // sendError('分享配置保存失败, 请重试');
  }
});

export function ShareConfigModal({ onRequestClose }: Props) {
  const isVipMember = useStore($isVip);
  const isVipProd = useStoreMap({
    store: $tourData,
    keys: [],
    fn: (data) => data.expired === 0,
  });
  const isVip = isVipMember || isVipProd;
  const isTeamAccount = useStore($isTeamAccount);

  const [navIndex, setNavIndex] = useState(0);
  const isDatePickerOpen = useToggle(false);
  const loading = useToggle(true);

  const [settings, setSettings] = useImmer({
    privilege: PrivilegeType.public,
    password: '',
    shareConfig: {},
  });

  const dataUrl = useTeamConfig(
    `${API_DOMAIN}/member/my/tour/${getTid()}`,
    `${API_DOMAIN}/team/app/tours/${getTid()}`
  );

  useEffect(() => {
    axios.get<TourData>(dataUrl).then((x) => {
      setSettings((draft) => {
        draft.privilege = x.privilege ?? PrivilegeType.public;
        draft.password = x.password ?? '';
        draft.shareConfig = (parseJsonString(x.shareConfig ?? '') ??
          {}) as ShareConfig;
      });
      loading.off();
    });
  }, [dataUrl]);

  const saveConfig = () => {
    // 对于不支持的作品，关闭弹窗后恢复上次的访问设置项
    if (!isVip && settings.privilege === PrivilegeType.password) {
      saveEvent({
        shareConfig: JSON.stringify(settings.shareConfig),
      });
    } else if (!isTeamAccount && settings.privilege === PrivilegeType.team) {
      saveEvent({
        shareConfig: JSON.stringify(settings.shareConfig),
      });
    } else {
      saveEvent({
        privilege: settings.privilege,
        password: settings.password,
        shareConfig: JSON.stringify(settings.shareConfig),
      });
    }
  };

  useEffect(() => {
    saveConfig();
  }, [settings]);

  return (
    <Modal
      isOpen
      style={{ width: 970, height: 620 }}
      shouldCloseOnOverlayClick={!isDatePickerOpen.isOn}
      onRequestClose={() => {
        saveConfig();
        onRequestClose();
      }}
      header={
        <div className="flex">
          <Title>分享设置</Title>
          <div className="ml-20 self-end text-orange">
            注：分享设置修改后即生效
          </div>
        </div>
      }
    >
      <div className="relative flex h-full">
        <div style={{ width: 220 }} className="border-r">
          <div className="pt-20 pb-20 pr-20">
            {navItems.map((x, i) => (
              <div
                key={x}
                className={cx(
                  'flex h-40 cursor-pointer items-center rounded px-10',
                  {
                    'bg-blue text-white': i === navIndex,
                  }
                )}
                onClick={() => {
                  if (i !== navIndex) setNavIndex(i);
                }}
              >
                {x}
                {x === '临时分享' && (
                  <img
                    src={IconVip}
                    alt=""
                    className="ml-5 h-16 w-16 flex-shrink-0"
                  />
                )}
                <Icon src={IconArrow} className="ml-auto" />
              </div>
            ))}
          </div>
        </div>
        {loading.isOn ? (
          <div className="flex-1" />
        ) : (
          <div className="flex-1">
            {navIndex === 0 && (
              <ShareBasic
                isVip={isVip}
                isTeamAccount={!!isTeamAccount}
                settings={settings}
                onChange={setSettings}
              />
            )}
            {navIndex === 1 && (
              <ShareTempLinks
                settings={settings}
                onPickerToggle={(isOn) => isDatePickerOpen.set(isOn)}
              />
            )}
            {navIndex === 2 && (
              <ShareInfoSetting settings={settings} onChange={setSettings} />
            )}
          </div>
        )}
        {/* {saveConfig.isLoading && ( */}
        {/*  <div className="absolute inset-0 cursor-progress bg-green bg-opacity-10" /> */}
        {/* )} */}
      </div>
    </Modal>
  );
}
